import { useCallback, useMemo, useState } from "react";
import { noop, partition } from "lodash";
import { useApolloClient } from "@apollo/client";

import Graph_QL, { DynamicDataObk, GqlHook_Returns, StateDataObject } from "..";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { filterByConnection } from "../filterByConnection";
import { BasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { gqlActions } from "../gqlData_Reducer";

import { DeviceInfoItem } from "./LocationFeedingSlices/deviceInfoSlice";
import { DeviceInfo, DeviceInfoResponse, GET_DEVICE_INFO } from "./GQLCalls/getDeviceInfoCall";
import { CreatePanelDevice, MAINTAIN_DEVICE_INFO, MaintainDeviceInfoParameters, MaintainDeviceInfoResponse, MaintainPanelDevice } from "./GQLCalls/maintainDeviceInfoCall";
import { DeviceRegistersResponse, GET_DEVICE_REGISTERS } from "./GQLCalls/getDeviceRegistersCall";

export const useDeviceInfo = (connection: Graph_QL.GQLCons): GqlHook_Returns<DeviceInfoItem, BasicQueryInfoInput> => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const [, setCursorId] = useState(-1);

  const DeviceInfoState = useAppSelector(({ graphQlData: { domain } }) => domain.deviceInfo);
  const { fullState } = useMemo(() => {
    const state = filterByConnection(DeviceInfoState, Graph_QL.ToArr(connection)) as Record<string, DeviceInfoItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = { ...data.data, domain: key };
        accumulator.data = accumulator.data.concat(value)
      }
      if (data.cpuOptions) {
        const value = { ...data.cpuOptions, domain: key }
        accumulator.cpuOptions = accumulator.cpuOptions.concat(value);
      }
      return accumulator
    }, { fullState: state, data: [], cpuOptions: [] } as StateDataObject<DeviceInfoItem>)
  }, [connection, DeviceInfoState]);

  const getDeviceInfo = async (input?: BasicQueryInfoInput, specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific?.connection ? specific.connection : connection.connection;
    try {
      client.query<DeviceInfoResponse>({ query: GET_DEVICE_INFO, variables: { ...usedConnection, input } })
        .then((e) => {
          if (e && e.data) {
            dispatch({ type: String(gqlActions.domain.deviceInfo.maintained), payload: { con: connection?.connection, data: e.data.device.info } });
            setCursorId(e.data.device.info.at(-1)?.device_id ?? -1)
          }
        }).catch((error) => { console.log(error); })
    } catch (error) {
      console.log(error);
    }
  }

  const getCpuOptions = useCallback(async (specific?: Graph_QL.GQLCons, offset?: number) => {
    const usedConnection = specific?.connection ? specific.connection : connection.connection;
    try {
      client.query<DeviceRegistersResponse>({ query: GET_DEVICE_REGISTERS, variables: { ...usedConnection, limit: 2000, offset: offset ? offset : 0 } })
        .then((response) => {
          if (response && response.data) {
            dispatch({ type: String(gqlActions.domain.deviceInfo.maintained), payload: { con: usedConnection, data: [], cpuOptions: response.data.device.registers } })
          }
        })
    } catch (error) {
      console.log(error);
    }
  }, [client, connection.connection, dispatch])

  const maintainEntries = async (changedData?: Partial<DynamicDataObk<Partial<DeviceInfo>[]>>) => {
    const newDeviceInfo = changedData?.data ? changedData.data[0] : undefined;

    if (!newDeviceInfo) {
      return;
    }
    const [newDevices, updatedDevices] = partition(changedData?.data, (deviceInfo) => (!Boolean(deviceInfo.device_id))) as [CreatePanelDevice[], MaintainPanelDevice[]];
    try {
      client.mutate<MaintainDeviceInfoResponse, MaintainDeviceInfoParameters>({ mutation: MAINTAIN_DEVICE_INFO, variables: { ...connection.connection, newDevices, updatedDevices } })
        .then((result) => {
          if (result && result.data) {
            dispatch({ type: String(gqlActions.domain.deviceInfo.maintained), payload: { con: connection?.connection, data: result.data.device.maintainInfo } });
          }
        }
        ).catch((error) => { console.log(error) })
    } catch {
      console.warn("The update failed -> No changes dispatched");
    }
  }

  return { call: getDeviceInfo, getCpuOptions, fetchMore: noop, deleteEntries: noop, data: fullState, maintainEntries }
} 

